import { useEffect, useRef } from 'react'

/**
 * The patient page's realtime fan-out.
 *
 * `PatientLayout` owns the ONE subscription to `patient_update/{patient_id}` and
 * puts the last event on the outlet context as `live`. Every tab consumes it
 * through this hook instead of opening its own channel — a patient page renders
 * up to a dozen sub-pages and a dozen WebSocket channels for events this rare
 * would be silly.
 *
 * The events come from Xano TABLE TRIGGERS (`patient_update - *`), not from the
 * endpoints, so they fire no matter who wrote the row: this CRM, the Bubble app,
 * the doctor portal, or a scheduled task. Payload:
 *
 *   { type, action, employee_id?, patient_id, id, branch_id?, treatment_id?, user_id? }
 *
 * `type` is the table that changed:
 *   patient · appointment · treatment · billing · chart · lab · call · ticket ·
 *   recall · promotion
 *
 * Self-echo is skipped in PatientLayout where `employee_id` is present. Three
 * tables can't say who wrote them — `patient` (no modified_by column), `chart`
 * (creator is a USER id, a different id space) and anything a cron touches — so
 * on those the writer's own page refetches too. That is a wasted request, never
 * a wrong screen.
 */

/**
 * Run `fn` when a live event of one of `types` arrives, debounced.
 *
 * The debounce is what makes a cascade cheap: adding a billing line fires
 * `billing` and then `patient` (the total_price trigger rewrites the patient
 * row), and both land inside one window.
 */
export function usePatientLive(live, types, fn, delay = 400) {
  const fnRef = useRef(fn)
  fnRef.current = fn
  const timer = useRef()

  useEffect(() => {
    if (!live || !types?.includes(live.type)) return
    clearTimeout(timer.current)
    timer.current = setTimeout(() => fnRef.current?.(live), delay)
    // `live` carries an `at` stamp, so two identical events in a row are still
    // two distinct objects and this effect re-runs for both.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live])

  useEffect(() => () => clearTimeout(timer.current), [])
}
